import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { DateRange } from "./dateRangePresets";

import { BottomSheet } from "../../../BottomSheet";
import { Button as ButtonV2 } from "../../../Button";
import { CheckIcon, ChevronRightIcon } from "../../../Icons";
import { MobileDateRangeBottomSheet } from "../../../MobileDateRange";
import { Typo } from "../../../Typo";
import {
  formatDateRangeLabel,
  matchDateRangePreset,
  useDateRangePresets,
} from "./dateRangePresets";

type FilterOption = { label: string; value: string };

type MobileFilterSectionProps = {
  label: string;
  options: FilterOption[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  selectAllLabel: string;
};

/** Collapsible "label + All/N selected trigger → checkbox list" section, one per facet (Sub IB, Status). */
const MobileFilterSection = ({
  label,
  options,
  selected,
  onChange,
  selectAllLabel,
}: MobileFilterSectionProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const allSelected = options.length > 0 && selected.size === options.length;
  const summary =
    selected.size === 0 || allSelected
      ? t("subIBReport_filterAll", "All")
      : t("subIBReport_filterNSelected", "{{count}} selected", {
          count: selected.size,
        });

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next);
  };

  const toggleAll = () => {
    onChange(allSelected ? new Set() : new Set(options.map((o) => o.value)));
  };

  return (
    <div className="border-t border-stroke-primary py-4 first:border-t-0 first:pt-0 last:pb-0">
      {/* my-2 (8px): matches the trading report's own expandable filter
          section (MobileFiltersBottomSheet.tsx) trigger button, stacking
          with this section's own 16px container padding for a 24px gap to
          the divider on both sides. */}
      <button
        type="button"
        className="my-2 flex w-full items-center justify-between"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        <Typo element="span" type="labelSM" textColor="text-text-primary">
          {label}
        </Typo>
        <span className="flex items-center gap-1">
          <Typo element="span" type="bodySM" textColor="text-text-selected">
            {summary}
          </Typo>
          <ChevronRightIcon
            className={`h-4 w-4 text-icon-tertiary transition-transform ${expanded ? "rotate-90" : ""}`}
            aria-hidden="true"
          />
        </span>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${expanded ? "mt-3 grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-3">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="peer sr-only"
              />
              <span className="flex h-5 w-5 items-center justify-center rounded border border-stroke-primary bg-surface-primary peer-checked:border-surface-inverse-primary peer-checked:bg-surface-inverse-primary">
                {allSelected && (
                  <CheckIcon className="h-3 w-3 text-text-inverse-primary" />
                )}
              </span>
              <Typo element="span" type="bodySM">
                {selectAllLabel}
              </Typo>
            </label>
            {options.map((option) => (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2"
              >
                <input
                  type="checkbox"
                  checked={selected.has(option.value)}
                  onChange={() => toggle(option.value)}
                  className="peer sr-only"
                />
                <span className="flex h-5 w-5 items-center justify-center rounded border border-stroke-primary bg-surface-primary peer-checked:border-surface-inverse-primary peer-checked:bg-surface-inverse-primary">
                  {selected.has(option.value) && (
                    <CheckIcon className="h-3 w-3 text-text-inverse-primary" />
                  )}
                </span>
                <Typo element="span" type="bodySM">
                  {option.label}
                </Typo>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

type MobileProgramFilterSheetProps = {
  open: boolean;
  onClose: () => void;
  onApply: (filters: {
    subIb: string[];
    status: string[];
    dateRange: DateRange;
  }) => void;
  subIbOptions: FilterOption[];
  statusOptions: FilterOption[];
  committedSubIb: string[];
  committedStatus: string[];
  committedDateRange: DateRange;
};

/**
 * Mobile Programs-tab filter sheet: payout period (presets + custom) + Sub
 * IB + status, staged and committed only on Apply. Payout period lives
 * inside this sheet on mobile — the desktop `FilterModal` has no date field
 * (the page's `DatepickerV2` owns it there) — a deliberate mobile-only
 * structural difference from the canvas, not a bug (see
 * PART-4052-analyse.md Risk #4).
 */
export const MobileProgramFilterSheet = ({
  open,
  onClose,
  onApply,
  subIbOptions,
  statusOptions,
  committedSubIb,
  committedStatus,
  committedDateRange,
}: MobileProgramFilterSheetProps) => {
  const { t, i18n } = useTranslation();
  const presets = useDateRangePresets();
  const [view, setView] = useState<"filter" | "custom">("filter");
  const [draftSubIb, setDraftSubIb] = useState<Set<string>>(new Set());
  const [draftStatus, setDraftStatus] = useState<Set<string>>(new Set());
  const [draftRange, setDraftRange] = useState<DateRange>(committedDateRange);

  useEffect(() => {
    if (!open) return;
    setDraftSubIb(new Set(committedSubIb));
    setDraftStatus(new Set(committedStatus));
    setDraftRange(committedDateRange);
    setView("filter");
    // Re-seed only when the sheet opens — re-running on every committed*
    // change would clobber in-progress edits while it's already open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedPreset = matchDateRangePreset(draftRange, presets);

  const handleReset = () => {
    setDraftSubIb(new Set());
    setDraftStatus(new Set());
    const defaultRange = presets.find((p) => p.id === "allTime")?.range;
    if (defaultRange) setDraftRange(defaultRange);
  };

  const handleApply = () => {
    onApply({
      subIb: Array.from(draftSubIb),
      status: Array.from(draftStatus),
      dateRange: draftRange,
    });
  };

  return (
    <>
      <BottomSheet
        isOpen={open && view === "filter"}
        onClose={onClose}
        title={t("subIBReport_filterModalTitle", "Filters")}
        isDraggable
        footer={
          <div className="flex gap-3">
            <ButtonV2
              variant="outline"
              size="md"
              className="flex-1"
              onClick={handleReset}
            >
              {t("subIBReport_filterClear", "Clear")}
            </ButtonV2>
            <ButtonV2
              variant="brand"
              size="md"
              className="flex-1"
              onClick={handleApply}
            >
              {t("subIBReport_filterApply", "Apply")}
            </ButtonV2>
          </div>
        }
      >
        <div className="flex flex-col">
          <div className="pb-4">
            <Typo element="span" type="labelSM" textColor="text-text-primary">
              {t("subIBReport_payoutPeriod", "Trading period")}
            </Typo>
            <Typo
              element="p"
              type="bodyXS"
              textColor="text-text-tertiary"
              className="mt-1"
            >
              {t(
                "subIBReport_payoutPeriodHint",
                "Total earning and your payout reflect trades placed in this range",
              )}
            </Typo>
            <div className="mt-3 flex flex-col gap-3">
              {presets.map((preset) => (
                <label
                  key={preset.id}
                  className="flex cursor-pointer items-center gap-3"
                >
                  <div className="relative">
                    <input
                      type="radio"
                      name="sm-filter-date"
                      checked={selectedPreset?.id === preset.id}
                      onChange={() => setDraftRange(preset.range)}
                      className="peer sr-only"
                    />
                    <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-stroke-primary bg-surface-primary peer-checked:border-surface-inverse-primary peer-checked:bg-surface-inverse-primary peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-stroke-focused">
                      {selectedPreset?.id === preset.id && (
                        <div className="h-3 w-3 rounded-full bg-surface-primary" />
                      )}
                    </div>
                  </div>
                  <Typo element="span" type="bodySM">
                    {preset.label}
                  </Typo>
                </label>
              ))}
              <button
                type="button"
                className="flex items-center gap-3 text-left"
                onClick={() => setView("custom")}
              >
                <div className="relative">
                  <input
                    type="radio"
                    name="sm-filter-date"
                    readOnly
                    checked={!selectedPreset}
                    className="peer sr-only"
                  />
                  <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-stroke-primary bg-surface-primary peer-checked:border-surface-inverse-primary peer-checked:bg-surface-inverse-primary peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-stroke-focused">
                    {!selectedPreset && (
                      <div className="h-3 w-3 rounded-full bg-surface-primary" />
                    )}
                  </div>
                </div>
                <span className="flex flex-1 flex-col">
                  <Typo element="span" type="bodySM">
                    {t("subIBReport_customDateRange", "Custom date range")}
                  </Typo>
                  {!selectedPreset && (
                    <Typo
                      element="span"
                      type="bodyXS"
                      textColor="text-text-secondary"
                    >
                      {formatDateRangeLabel(draftRange, presets, i18n.language)}
                    </Typo>
                  )}
                </span>
                <ChevronRightIcon
                  className="h-4 w-4 text-icon-primary"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <MobileFilterSection
            label={t("subIBReport_filterSubIb", "Sub IB")}
            options={subIbOptions}
            selected={draftSubIb}
            onChange={setDraftSubIb}
            selectAllLabel={t("subIBReport_filterSelectAll", "Select all")}
          />
          <MobileFilterSection
            label={t("subIBReport_filterStatus", "Program status")}
            options={statusOptions}
            selected={draftStatus}
            onChange={setDraftStatus}
            selectAllLabel={t("subIBReport_filterSelectAll", "Select all")}
          />
        </div>
      </BottomSheet>

      <MobileDateRangeBottomSheet
        isOpen={open && view === "custom"}
        onClose={onClose}
        onBack={() => setView("filter")}
        onApply={(range) => {
          setDraftRange(range);
          setView("filter");
        }}
        // "All time" isn't a bounded range — seeding the custom picker with
        // its 2021-to-today span would misrepresent it as one. Open blank
        // instead when that's the currently selected preset (PART-4130).
        initialRange={selectedPreset?.id === "allTime" ? null : draftRange}
        defaultRange={presets.find((p) => p.id === "last30days")?.range ?? null}
        isDraggable
      />
    </>
  );
};
